/**
 * Permission Detail Sheet
 * 
 * A side drawer/sheet that opens when clicking "Custom Access" on a permission.
 * Contains tabs for:
 * - Item Permissions (filter rules for which items can be accessed)
 * - Field Permissions (which fields are accessible)
 * - Field Validation (validation rules for fields)
 * - Field Presets (default values for create/update)
 */

import { useState, useEffect, useMemo } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import type { Permission } from '@/stores/access-control';
import { usePermission } from '@/hooks/access-control';
import { PermissionTabs } from './PermissionTabs';
import { ItemPermissionsTab } from './ItemPermissionsTab';
import { FieldPermissionsTab } from './FieldPermissionsTab';
import { FieldValidationTab } from './FieldValidationTab';
import { Button } from '@/components/ui/button';

export interface PermissionDetailSheetProps {
    isOpen: boolean;
    /** Permission ID for existing permission, or null for new permission */
    permissionId: string | null;
    /** Fallback permission data (collection, action) for new permissions */
    permissionData: {
        collection: string;
        action: string;
    } | null;
    policyId: string;
    policyName: string;
    /** Fields that are required by app minimal permissions */
    appMinimalFields?: string[] | null;
    onClose: () => void;
    onSave: (updates: Partial<Permission>) => Promise<void>;
}

type TabId = 'permissions' | 'fields' | 'validation';

interface Tab {
    id: TabId;
    label: string;
    hasValue: boolean;
    availableFor: string[];
}

export function PermissionDetailSheet({
    isOpen,
    permissionId,
    permissionData,
    policyId,
    policyName,
    appMinimalFields,
    onClose,
    onSave,
}: PermissionDetailSheetProps) {
    const [activeTab, setActiveTab] = useState<TabId>('permissions');
    const [localPermission, setLocalPermission] = useState<Partial<Permission>>({});
    const [isSaving, setIsSaving] = useState(false);

    // Fetch fresh permission data from API when opening
    const { data: fetchedPermission, isLoading: isLoadingPermission } = usePermission(
        isOpen && permissionId ? permissionId : undefined
    );

    // Build the effective permission from fetched data or create new
    const permission: Permission | null = useMemo(() => {
        if (fetchedPermission) {
            return fetchedPermission as Permission;
        }
        if (permissionData && !permissionId) {
            // New permission
            return {
                id: '',
                policy: policyId,
                collection: permissionData.collection,
                action: permissionData.action as Permission['action'],
                fields: ['*'],
                permissions: null,
                validation: null,
                presets: null,
            };
        }
        return null;
    }, [fetchedPermission, permissionData, permissionId, policyId]);

    // Reset local state when permission changes
    useEffect(() => {
        if (permission) {
            setLocalPermission({
                permissions: permission.permissions,
                fields: permission.fields,
                validation: permission.validation,
            });
        }
    }, [permission]);

    // Reset to first available tab when action changes
    useEffect(() => {
        if (permission) {
            const availableTabs = getAvailableTabs();
            if (availableTabs.length > 0 && !availableTabs.find(t => t.id === activeTab)) {
                setActiveTab(availableTabs[0]?.id ?? 'permissions');
            }
        }
    }, [permission?.action]);

    const getAvailableTabs = (): Tab[] => {
        if (!permission) return [];

        const action = permission.action;
        const tabs: Tab[] = [];

        // Item Permissions - for read, update, delete, share
        if (['read', 'update', 'delete', 'share'].includes(action)) {
            tabs.push({
                id: 'permissions',
                label: 'Item Permissions',
                hasValue: localPermission.permissions !== null &&
                    Object.keys(localPermission.permissions || {}).length > 0,
                availableFor: ['read', 'update', 'delete', 'share'],
            });
        }

        // Field Permissions - for create, read, update
        if (['create', 'read', 'update'].includes(action)) {
            tabs.push({
                id: 'fields',
                label: 'Field Permissions',
                hasValue: localPermission.fields !== null &&
                    !localPermission.fields?.includes('*'),
                availableFor: ['create', 'read', 'update'],
            });
        }

        // Field Validation - for create, update
        if (['create', 'update'].includes(action)) {
            tabs.push({
                id: 'validation',
                label: 'Field Validation',
                hasValue: localPermission.validation !== null &&
                    Object.keys(localPermission.validation || {}).length > 0,
                availableFor: ['create', 'update'],
            });
        }

        return tabs;
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(localPermission);
            onClose();
        } catch (error) {
            console.error('Failed to save permission:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdatePermission = (updates: Partial<Permission>) => {
        setLocalPermission(prev => ({ ...prev, ...updates }));
    };

    if (!isOpen) return null;

    // Show loading state while fetching permission
    if (isLoadingPermission && permissionId) {
        return (
            <>
                {/* Backdrop */}
                <div
                    className="fixed inset-0 z-40 bg-black/50 transition-opacity"
                    onClick={onClose}
                />

                {/* Loading Sheet */}
                <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl">
                    <div className="flex flex-1 flex-col items-center justify-center bg-white">
                        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                        <span className="mt-4 text-gray-500">Loading permission...</span>
                    </div>
                </div>
            </>
        );
    }

    if (!permission) return null;

    const tabs = getAvailableTabs();
    const currentTab = tabs.find(t => t.id === activeTab);
    const modalTitle = `${policyName || 'Public'} → ${permission.collection} → ${permission.action}`;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-40 bg-black/50 transition-opacity"
                onClick={onClose}
            />

            {/* Sheet */}
            <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-4xl">
                {/* Sidebar Tabs */}
                <div className="flex w-48 flex-shrink-0 flex-col border-r border-gray-200 bg-gray-100">
                    <div className="flex h-14 items-center border-b border-gray-200 px-4">
                        <span className="text-sm font-medium text-gray-500">
                            {currentTab?.label}
                        </span>
                    </div>
                    <PermissionTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={(tabId) => setActiveTab(tabId as TabId)}
                    />
                </div>

                {/* Main Content */}
                <div className="flex flex-1 flex-col bg-white min-w-0 overflow-hidden">
                    {/* Header */}
                    <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-gray-200 px-6">
                        <h2 className="text-lg font-semibold text-gray-900 truncate">
                            {modalTitle}
                        </h2>
                        <div className="flex flex-shrink-0 items-center gap-2">
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                size="icon"
                                className="rounded-full"
                                title="Save"
                            >
                                {isSaving ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <Check className="h-5 w-5" />
                                )}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="rounded-full"
                                title="Close"
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {activeTab === 'permissions' && (
                            <ItemPermissionsTab
                                permission={permission}
                                localPermission={localPermission}
                                policyName={policyName}
                                onUpdate={handleUpdatePermission}
                            />
                        )}
                        {activeTab === 'fields' && (
                            <FieldPermissionsTab
                                permission={permission}
                                localPermission={localPermission}
                                policyName={policyName}
                                appMinimalFields={appMinimalFields}
                                onUpdate={handleUpdatePermission}
                            />
                        )}
                        {activeTab === 'validation' && (
                            <FieldValidationTab
                                permission={permission}
                                localPermission={localPermission}
                                policyName={policyName}
                                onUpdate={handleUpdatePermission}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
